import React, { MouseEventHandler } from "react";
import { Link } from "react-router-dom";
import Loader from "../Common/Loader";

interface GeneralButtonProps {
  type?: "button" | "submit" | "reset";
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  title?: React.ReactNode;
  icon?: string;
  color?:
    | "lite"
    | "blue"
    | "red"
    | "liteRed"
    | "white"
    | "gray"
    | "main"
    | "yellow"
    | "liteGray"
    | "green"
    | "primaryBg";
  fontColor?: string;
  block?: boolean;
  disabled?: boolean;
  route?: string;
  className?: string;
  ariaLabel?: string;
  styleClasses?: string;
  form?: string;
  titleClass?: string;
  isLoading?: boolean;
  isActive?: boolean;
  iconClass?: string;
}

const GeneralButton: React.FC<GeneralButtonProps> = ({
  type = "button",
  onClick,
  title,
  icon,
  fontColor = "primary",
  color = "blue",
  block = false,
  disabled = false,
  route,
  className = "",
  ariaLabel,
  styleClasses = "",
  form,
  isLoading,
  titleClass,
  isActive = false,
  iconClass,
}) => {
  const colorClasses = {
    blue: "bg-primary md:pl-16 md:pr-16 hover:bg-primaryHovered text-white",
    red: "bg-error hover:bg-error text-white ",
    yellow: "bg-orange text-white",
    liteRed: "bg-error-00 text-error hover:opacity-80 md:px-16",
    gray: "bg-radio text-primary-text hover:opacity-80 md:px-16 !font-bold",
    liteGray: "bg-InputFieldColor text-body-text text-sm !font-bold py-1.5",
    lite: `bg-primary-click text-primary text-${fontColor} hover:bg-primary-bg`,
    white: `bg-white text-primary-textFont border !border-body-text`,
    main: `main_warper !py-2.5`,
    primaryBg: `bg-primary-bg text-dark`,
    green: `bg-positive text-white`,
  };

  const selectedColorClass = colorClasses[color] || colorClasses.lite;

  const baseClasses = [
    "py-2 px-4",
    "whitespace-nowrap",
    "transition ease-in duration-200",
    "text-center",
    "font-semibold",
    "rounded-lg",
    "flex items-center justify-center gap-2",
    disabled
      ? "bg-primary-bg text-primary-text cursor-not-allowed opacity-30"
      : isActive
        ? "bg-primary-click text-primary font-bold border border-primary !p-2.5"
        : selectedColorClass,
    block ? "w-full h-10 text-xl" : "w-auto max-xl:text-sm text-base",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const ButtonContent = (
    <button
      form={form}
      disabled={disabled || isLoading}
      type={type}
      onClick={onClick}
      className={`${baseClasses} ${styleClasses} ${isLoading ? "flex gap-4" : ""}`}
      aria-label={ariaLabel}
    >
      {isLoading && (
        <div className="w-4">
          <Loader
            size="small"
            className={color == "lite" ? "" : "!w-4 !border-primary-bg"}
          />
        </div>
      )}
      {icon && (
        <img
          className={`max-w-6 !max-h-6 ${isActive ? "brightness-0" : ""} ${iconClass}`}
          style={
            isActive
              ? {
                  filter:
                    "brightness(0) saturate(100%) invert(28%) sepia(85%) saturate(2000%) hue-rotate(210deg) brightness(95%) contrast(95%)",
                }
              : {}
          }
          src={icon}
          alt=""
        />
      )}
      {title && <span className={titleClass}>{title}</span>}
    </button>
  );

  return route ? (
    <Link className={`h-fit ${className}`} to={route}>
      {ButtonContent}
    </Link>
  ) : (
    ButtonContent
  );
};

export default GeneralButton;
